import React, { useState } from "react";
import {
  ShieldCheck,
  Search,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Copy,
  Download,
  ListChecks,
  User,
  Building2,
  MapPin,
  Fingerprint,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { MoreTools } from "@/components/MoreTools";
import { Seo } from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const faqs = [
  {
    q: "What is a valid NPI number example?",
    a: "A valid NPI number example is 1437702123 — a real, active 10-digit NPI in the CMS NPPES registry (issued to a physician assistant in New Hampshire). Any valid NPI is exactly 10 digits and passes the CMS Luhn checksum with the '80840' health-industry prefix. Paste that number into the validator above to see a full valid-NPI response — checksum passes and full provider details are returned from NPPES.",
  },
  {
    q: "How do I know if an NPI number is valid?",
    a: "To know if an NPI number is valid, do two things: (1) confirm it's exactly 10 digits and passes the CMS Luhn checksum (this rules out typos and made-up numbers) and (2) confirm it exists as an active record in the CMS NPPES registry. This tool does both in one call — showing a green 'valid NPI' banner only when both checks pass. A number can pass the checksum but not exist in NPPES (a synthetic or unissued number), so both checks matter.",
  },
  {
    q: "What is an NPI validator?",
    a: "An NPI validator checks whether a National Provider Identifier (NPI) is a real, active provider identifier. Hewto.ai's NPI validator runs the CMS Luhn checksum with the '80840' health-industry prefix and then looks the NPI up in the CMS NPPES public registry to return the provider's name, address, taxonomy, and status — instantly and free.",
  },
  {
    q: "How does the NPI checksum work?",
    a: "The NPI is a 10-digit number where the 10th digit is a Luhn (ISO/IEC 7812) check digit calculated over the first 9 digits prefixed with '80840' (the CMS health-industry issuer identifier). To validate: prefix the first 9 digits with 80840 → double every second digit from the right → sum the digits → the check digit equals (10 − (sum mod 10)) mod 10. If it matches the 10th digit, the checksum is valid.",
  },
  {
    q: "Is this NPI lookup tool free?",
    a: "Yes. Hewto.ai's NPI lookup and NPI checker is 100% free — no signup, no API key. It uses the free CMS NPPES public API to return provider data, and Hewto.ai runs the checksum for you so you can validate any NPI in one place.",
  },
  {
    q: "What's the difference between an NPI-1 and NPI-2?",
    a: "NPI-1 is an individual provider (a doctor, dentist, PA, nurse practitioner, therapist, etc.). NPI-2 is an organization (a hospital, clinic, group practice, DME supplier, or pharmacy). Both use the same 10-digit format and Luhn checksum, and both are queryable through this NPI validator.",
  },
  {
    q: "Can I validate multiple NPIs at once?",
    a: "Yes. Use the Bulk tab to paste up to 100 NPIs (one per line or comma-separated). The tool returns a table with checksum validity, provider name, entity type, primary taxonomy, state, and NPPES status for each — and you can export the results to CSV in one click.",
  },
  {
    q: "Where does this NPI lookup pull data from?",
    a: "Data is pulled live from the CMS NPPES NPI Registry (National Plan and Provider Enumeration System) — the official US registry maintained by the Centers for Medicare & Medicaid Services. It's the same source that clearinghouses, payers, and RCM platforms use.",
  },
  {
    q: "Why does an NPI show as 'checksum valid' but 'not found'?",
    a: "A valid checksum means the 10-digit number is mathematically well-formed, but the NPI may never have been issued by CMS, may be a test number, or may have been deactivated. If NPPES returns no record, we surface that clearly so you know the NPI shouldn't be used on a claim.",
  },
  {
    q: "Does the NPI validator store the NPIs I look up?",
    a: "No. Lookups are proxied through Hewto.ai's API to the CMS NPPES public registry and results are returned in real time. We don't persist the NPIs you validate or the results.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": ["WebApplication", "SoftwareApplication"],
  name: "Hewto.ai NPI Validator & Lookup",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Free NPI validator and NPI lookup — validates the CMS Luhn checksum and returns provider name, address, taxonomy, and status from the CMS NPPES registry.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

const parseNPIList = (raw) =>
  Array.from(
    new Set(
      (raw || "")
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );

const toCsv = (rows) => {
  const header = [
    "npi",
    "checksum_valid",
    "found",
    "entity_type",
    "name",
    "status",
    "credential",
    "taxonomy",
    "city",
    "state",
    "postal_code",
    "phone",
    "error",
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.npi,
        r.checksum_valid,
        r.found,
        r.entity_type,
        r.name,
        r.status,
        r.credential,
        r.taxonomy?.desc,
        r.address?.city,
        r.address?.state,
        r.address?.postal_code,
        r.address?.phone,
        r.error,
      ]
        .map(esc)
        .join(","),
    );
  }
  return lines.join("\n");
};

const SingleResult = ({ data }) => {
  if (!data) return null;
  const ok = data.checksum_valid && data.found;
  const partial = data.checksum_valid && !data.found;
  const bad = !data.checksum_valid;

  return (
    <div
      data-testid="npi-single-result"
      className={`mt-6 overflow-hidden rounded-2xl border ${
        bad ? "border-red-200 bg-red-50" : ok ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50"
      }`}
    >
      <div className="flex items-start gap-3 border-b border-black/5 p-5">
        {bad ? (
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        ) : ok ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        ) : (
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        )}
        <div>
          <p
            data-testid="npi-single-status"
            className={`text-sm font-semibold ${
              bad ? "text-red-700" : ok ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            {bad
              ? "Invalid NPI — checksum failed"
              : ok
                ? "Valid NPI — active in NPPES registry"
                : "Checksum valid, but no NPPES record found"}
          </p>
          <p className="mt-1 font-mono text-xs text-slate-500">NPI {data.npi}</p>
        </div>
      </div>
      {ok && (
        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              {data.entity_type === "Organization" ? <Building2 className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
              {data.entity_type}
            </p>
            <p data-testid="npi-single-name" className="mt-2 text-lg font-semibold text-slate-900">{data.name || "—"}</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Status</dt>
                <dd className="font-medium text-slate-900">{data.status === "A" ? "Active" : data.status || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Enumeration date</dt>
                <dd className="font-medium text-slate-900">{data.enumeration_date || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Last updated</dt>
                <dd className="font-medium text-slate-900">{data.last_updated || "—"}</dd>
              </div>
              {data.credential && (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Credential</dt>
                  <dd className="font-medium text-slate-900">{data.credential}</dd>
                </div>
              )}
            </dl>
          </div>
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <Fingerprint className="h-3.5 w-3.5" /> Taxonomy
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{data.taxonomy?.desc || "—"}</p>
            <p className="mt-1 font-mono text-xs text-slate-500">
              {data.taxonomy?.code || ""} {data.taxonomy?.state ? `· ${data.taxonomy.state}` : ""}
              {data.taxonomy?.license ? ` · License ${data.taxonomy.license}` : ""}
            </p>
            <p className="mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <MapPin className="h-3.5 w-3.5" /> Practice address
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              {data.address?.line1}
              {data.address?.line2 ? `, ${data.address.line2}` : ""}
              <br />
              {[data.address?.city, data.address?.state, data.address?.postal_code].filter(Boolean).join(", ")}
              {data.address?.phone ? (
                <>
                  <br />
                  <span className="font-mono text-xs">{data.address.phone}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>
      )}
      {partial && (
        <p className="p-6 text-sm text-slate-700">
          The checksum passes, so the number is well-formed — but the CMS NPPES registry has no record for it. It may
          be a synthetic/test NPI, may never have been issued, or may be deactivated.
        </p>
      )}
      {bad && (
        <p className="p-6 text-sm text-red-700">
          {data.error || "This NPI is not 10 digits or does not pass the CMS Luhn checksum. Don't use it on a claim."}
        </p>
      )}
    </div>
  );
};

const BulkResults = ({ rows }) => {
  if (!rows?.length) return null;
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="max-h-[520px] overflow-auto">
        <table data-testid="npi-bulk-table" className="w-full min-w-[720px] text-left text-sm">
          <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">NPI</th>
              <th className="p-3">Checksum</th>
              <th className="p-3">Registry</th>
              <th className="p-3">Name</th>
              <th className="p-3">Type</th>
              <th className="p-3">Taxonomy</th>
              <th className="p-3">State</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-3 font-mono text-xs text-slate-700">{r.npi}</td>
                <td className="p-3">
                  {r.checksum_valid ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> Valid
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                      <AlertTriangle className="h-3 w-3" /> Invalid
                    </span>
                  )}
                </td>
                <td className="p-3">
                  {r.found ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                      Found
                    </span>
                  ) : r.checksum_valid ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      Not in NPPES
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3 text-slate-900">{r.name || "—"}</td>
                <td className="p-3 text-slate-600">{r.entity_type || "—"}</td>
                <td className="p-3 text-slate-600">{r.taxonomy?.desc || "—"}</td>
                <td className="p-3 text-slate-600">{r.address?.state || r.taxonomy?.state || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const NPIValidator = () => {
  const [single, setSingle] = useState("");
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);

  const [bulk, setBulk] = useState("");
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const runSingle = async () => {
    const num = single.replace(/\D/g, "").slice(0, 10);
    if (!num) {
      toast.error("Enter an NPI number");
      return;
    }
    setSingleLoading(true);
    setSingleResult(null);
    try {
      const res = await fetch(`${API}/npi/lookup?number=${num}`);
      const data = await res.json();
      setSingleResult(data);
    } catch (e) {
      toast.error("Lookup failed — please try again");
    } finally {
      setSingleLoading(false);
    }
  };

  const runBulk = async () => {
    const numbers = parseNPIList(bulk);
    if (!numbers.length) {
      toast.error("Paste at least one NPI");
      return;
    }
    if (numbers.length > 100) {
      toast.error("Bulk lookup is limited to 100 NPIs at a time");
      return;
    }
    setBulkLoading(true);
    setBulkResults([]);
    try {
      const res = await fetch(`${API}/npi/lookup/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numbers }),
      });
      const data = await res.json();
      setBulkResults(data.results || []);
      toast.success(`Validated ${data.count} NPIs`);
    } catch (e) {
      toast.error("Bulk lookup failed — please try again");
    } finally {
      setBulkLoading(false);
    }
  };

  const exportCsv = () => {
    const csv = toCsv(bulkResults);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `npi-validation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copySingle = () => {
    if (!singleResult) return;
    navigator.clipboard.writeText(JSON.stringify(singleResult, null, 2));
    toast.success("Result copied as JSON");
  };

  return (
    <PageTransition>
      <Seo
        title="NPI Validator & NPI Number Validation — Free CMS NPPES Lookup | Hewto.ai"
        description="Free NPI validation tool — check if any 10-digit NPI is a valid NPI number. Hewto.ai runs the CMS Luhn checksum for NPI number validation and pulls live provider details from the CMS NPPES registry. See a valid NPI number example, run single or bulk NPI validation (up to 100) with CSV export."
        keywords="npi validation, npi number validation, valid npi, valid npi number, valid npi number example, npi validator, npi lookup, npi checker, npi number lookup, npi verification, nppes lookup, cms npi lookup, healthcare provider lookup, npi 1 lookup, npi 2 lookup, npi taxonomy lookup, bulk npi validator, npi luhn checksum, npi checksum validator, free npi validator, provider npi lookup api, check npi number, verify npi number, npi format validation"
        canonical="https://www.hewto.ai/tools/npi-validator"
        jsonLd={[faqJsonLd, softwareJsonLd]}
      />

      <PageHero
        eyebrow="Free tool · NPI Validator"
        title="NPI Validator & Number Validation"
        subtitle="Run NPI validation on any US healthcare NPI in one click — Hewto.ai's free NPI number validation tool runs the CMS Luhn checksum and pulls live provider details from the CMS NPPES registry. Check if a number is a valid NPI, see a valid NPI number example below, and run single or bulk NPI validation (up to 100 NPIs) with CSV export. No signup."
      />

      <section className="mx-auto max-w-4xl px-5 pb-20 sm:px-8">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-brand/5 sm:p-8">
            <Tabs defaultValue="single">
              <TabsList data-testid="npi-tabs" className="grid w-full grid-cols-2 rounded-full bg-slate-100 p-1">
                <TabsTrigger data-testid="npi-tab-single" value="single" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm">
                  Single NPI
                </TabsTrigger>
                <TabsTrigger data-testid="npi-tab-bulk" value="bulk" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm">
                  Bulk (up to 100)
                </TabsTrigger>
              </TabsList>

              <TabsContent value="single" className="mt-6">
                <label htmlFor="npi-single-input" className="text-sm font-semibold text-slate-700">
                  Enter a 10-digit NPI
                </label>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <Input
                    id="npi-single-input"
                    data-testid="npi-single-input"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="e.g. 1437702123"
                    value={single}
                    onChange={(e) => setSingle(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    onKeyDown={(e) => e.key === "Enter" && runSingle()}
                    className="h-12 rounded-xl font-mono text-base"
                  />
                  <Button
                    data-testid="npi-single-validate-btn"
                    onClick={runSingle}
                    disabled={singleLoading || single.length !== 10}
                    className="h-12 rounded-xl bg-brand px-6 font-semibold text-white hover:bg-brand-600"
                  >
                    {singleLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating</>
                    ) : (
                      <><Search className="mr-2 h-4 w-4" /> Validate NPI</>
                    )}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  We validate the CMS Luhn checksum, then look the NPI up in the NPPES registry. Nothing is stored.
                </p>

                <SingleResult data={singleResult} />

                {singleResult && (
                  <div className="mt-4">
                    <Button
                      data-testid="npi-single-copy-btn"
                      variant="outline"
                      onClick={copySingle}
                      className="rounded-full"
                    >
                      <Copy className="mr-2 h-4 w-4" /> Copy JSON
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="bulk" className="mt-6">
                <label htmlFor="npi-bulk-input" className="text-sm font-semibold text-slate-700">
                  Paste up to 100 NPIs — one per line, or comma-separated
                </label>
                <Textarea
                  id="npi-bulk-input"
                  data-testid="npi-bulk-input"
                  rows={6}
                  placeholder={"1437702123\n1234567893\n1023011178"}
                  value={bulk}
                  onChange={(e) => setBulk(e.target.value)}
                  className="mt-2 rounded-xl font-mono text-sm"
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button
                    data-testid="npi-bulk-validate-btn"
                    onClick={runBulk}
                    disabled={bulkLoading || !bulk.trim()}
                    className="h-11 rounded-xl bg-brand px-6 font-semibold text-white hover:bg-brand-600"
                  >
                    {bulkLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating</>
                    ) : (
                      <><ListChecks className="mr-2 h-4 w-4" /> Validate all</>
                    )}
                  </Button>
                  <span className="text-xs text-slate-500">
                    {parseNPIList(bulk).length} unique NPI{parseNPIList(bulk).length === 1 ? "" : "s"} detected
                  </span>
                  {bulkResults.length > 0 && (
                    <Button
                      data-testid="npi-bulk-csv-btn"
                      variant="outline"
                      onClick={exportCsv}
                      className="ml-auto rounded-full"
                    >
                      <Download className="mr-2 h-4 w-4" /> Export CSV
                    </Button>
                  )}
                </div>

                <BulkResults rows={bulkResults} />
              </TabsContent>
            </Tabs>
          </div>
        </Reveal>
      </section>

      {/* WHAT IS AN NPI */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Background
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                What is an NPI?
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                The National Provider Identifier (NPI) is a unique 10-digit identifier issued by the
                Centers for Medicare & Medicaid Services (CMS) to every healthcare provider and
                organization in the United States. It's required on every HIPAA-covered electronic
                transaction — CMS-1500, UB-04, ADA J430D, and their EDI 837 equivalents.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                {[
                  "NPI-1: individual providers (doctors, dentists, PAs, NPs, therapists)",
                  "NPI-2: organizations (hospitals, clinics, group practices, DME, pharmacies)",
                  "Issued and maintained by CMS NPPES — the National Plan and Provider Enumeration System",
                  "Required on Boxes 24J / 31-33 of the CMS-1500 and Boxes 48-58 of the ADA J430D",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Under the hood
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                How the NPI checksum works
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                The 10th digit of every NPI is a Luhn (ISO/IEC 7812) check digit calculated over
                the first 9 digits, prefixed with <span className="font-mono">80840</span> — the CMS
                issuer identifier for the US health-industry range.
              </p>
              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-950 p-6 shadow-lg">
                <pre className="whitespace-pre text-xs leading-relaxed text-slate-200">
{`# Example: 1437702123
1. Prefix first 9 digits with '80840'
   → 80840 143770212
2. Reverse and double every 2nd digit
3. If a doubled digit > 9, subtract 9
4. Sum every digit
5. check = (10 - (sum mod 10)) mod 10
6. Compare with NPI's 10th digit
   → matches 3 → valid ✓`}
                </pre>
              </div>
              <p className="mt-4 text-sm text-slate-500">
                A valid checksum doesn't guarantee the NPI is real — it only means the number is
                mathematically well-formed. Always confirm against the CMS NPPES registry (this tool
                does both, in one call).
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY VALIDATE */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Sparkles className="h-3.5 w-3.5" /> Why it matters
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Why validate every NPI before it hits a claim
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: ShieldCheck,
              title: "Fewer denials",
              desc: "Invalid or deactivated NPIs are a top cause of claim denials. Catching them upfront saves days of A/R rework.",
            },
            {
              icon: CheckCircle2,
              title: "Cleaner 837s",
              desc: "Every 837P / 837I / 837D file needs valid NPIs in Loop 2010AA and 2310B. Validate before you generate the EDI.",
            },
            {
              icon: ListChecks,
              title: "Roster hygiene",
              desc: "Bulk-check your provider roster or referral list monthly to catch retirements, deactivations, and taxonomy changes.",
            },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            NPI validator FAQ
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Common NPI lookup questions
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8" data-testid="npi-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger
                  data-testid={`npi-faq-trigger-${i}`}
                  className="text-left text-base font-semibold text-slate-900 hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <MoreTools current="/tools/npi-validator" />

      <CTABand />
    </PageTransition>
  );
};

export default NPIValidator;
